<template>
  <div class="recharge">
    <h1 class="page-title">积分充值</h1>
    <p class="page-desc">
      <!-- 与主站同一套积分账户与收银路由（经 <code class="mono">open.pluskeji.cn</code>）。
      充值后 <code class="mono">/idoing_*</code> 即可扣费；支付方式：支付宝扫码。 -->
    </p>

    <div class="recharge__balance panel">
      <div>
        <div class="recharge__label">当前可用积分</div>
        <div class="recharge__score mono">{{ balanceText }}</div>
      </div>
      <button type="button" class="btn btn-ghost" :disabled="profileLoading" @click="loadProfile">
        {{ profileLoading ? '刷新中…' : '刷新余额' }}
      </button>
    </div>

    <p v-if="error" class="flash is-err">{{ error }}</p>
    <p v-if="packsLoading" class="muted">正在加载充值档位…</p>

    <div v-else class="recharge__grid">
      <button
        v-for="pack in packs"
        :key="pack.id"
        type="button"
        class="pack panel"
        :class="{ 'is-selected': selectedId === pack.id, 'is-featured': pack.featured }"
        @click="selectedId = pack.id"
      >
        <span v-if="pack.memberBonus" class="pack__badge">{{ pack.memberBonus }}</span>
        <div class="pack__points mono">
          <span v-if="pack.pointsOld" class="pack__old">{{ formatPoints(pack.pointsOld) }}</span>
          {{ formatPoints(pack.points) }}
        </div>
        <div class="pack__unit">积分</div>
        <div class="pack__price">¥{{ pack.price }}</div>
      </button>
    </div>

    <div class="recharge__actions">
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!selectedPack || submitting"
        @click="onPay"
      >
        {{ submitting ? '下单中…' : payButtonText }}
      </button>
      <router-link class="btn btn-ghost" to="/usage">查看消耗明细</router-link>
    </div>

    <p class="recharge__note">
      * 支付后不退不换。问题反馈：
      <a href="mailto:pluskeji@126.com">pluskeji@126.com</a>
    </p>

    <PayQrModal
      :visible="payVisible"
      :title="payTitle"
      :price-main="payPrice"
      :qr-code="payQr"
      :expire-seconds="payExpire"
      @close="closePay"
      @expired="onPayExpired"
    />
  </div>
</template>

<script>
import PayQrModal from '../components/PayQrModal.vue'
import {
  fetchCreditRechargePacks,
  createScoreOrder,
  fetchCreditsUserProfile,
  getApiErrorMessage
} from '../utils/creditsRechargeApi'
import {
  resolveVipOrderPayment,
  buildCreditsPayModalPayload,
  getVipOrderPaymentFailureMessage,
  scanAlipayPayment,
  isAlipayPaymentPaid
} from '../utils/vipPayApi'
import { showErrorToast, showSuccessToast } from '../utils/toast'

const PAY_POLL_MS = 2500

export default {
  name: 'Recharge',
  components: { PayQrModal },
  data() {
    return {
      packs: [],
      packsLoading: false,
      selectedId: '',
      profile: null,
      profileLoading: false,
      submitting: false,
      error: '',
      payVisible: false,
      payTitle: '',
      payPrice: '0',
      payQr: '',
      payExpire: 900,
      payOrderId: null,
      payPollTimer: null,
      payPollInFlight: false
    }
  },
  computed: {
    balanceText() {
      if (!this.profile) return '—'
      return this.formatPoints(this.profile.score)
    },
    selectedPack() {
      return this.packs.find(p => p.id === this.selectedId) || null
    },
    payButtonText() {
      const p = this.selectedPack
      if (!p) return '请选择档位'
      return `去支付 ¥${p.price}`
    }
  },
  mounted() {
    this.loadPacks()
    this.loadProfile()
  },
  beforeDestroy() {
    this.stopPayPoll()
  },
  methods: {
    formatPoints(n) {
      const v = Number(n)
      if (!Number.isFinite(v)) return '0'
      return v.toLocaleString('en-US')
    },
    async loadPacks() {
      this.packsLoading = true
      this.error = ''
      try {
        this.packs = await fetchCreditRechargePacks()
        if (!this.selectedId && this.packs.length) {
          this.selectedId = this.packs[0].id
        }
      } catch (e) {
        this.error = getApiErrorMessage(e, '加载充值档位失败')
      } finally {
        this.packsLoading = false
      }
    },
    async loadProfile() {
      this.profileLoading = true
      try {
        this.profile = await fetchCreditsUserProfile()
      } catch (e) {
        /* 余额展示失败不阻断充值 */
      } finally {
        this.profileLoading = false
      }
    },
    async onPay() {
      const pack = this.selectedPack
      if (!pack) {
        showErrorToast('请选择充值档位')
        return
      }
      if (!pack.skuId) {
        showErrorToast('该档位暂未配置商品，请稍后再试或联系客服')
        return
      }
      if (this.submitting) return
      this.submitting = true
      this.error = ''
      try {
        const order = await createScoreOrder({ skuId: pack.skuId })
        const pay = resolveVipOrderPayment(order)
        if (pay.qrCode) {
          const modal = buildCreditsPayModalPayload(order, pack)
          this.payOrderId = pay.orderId || modal.orderId
          this.payTitle = modal.title
          this.payPrice = modal.priceMain
          this.payQr = modal.qrCode
          this.payExpire = modal.expireSeconds
          this.payVisible = true
          this.$nextTick(() => this.startPayPoll())
          return
        }
        if (pay.payUrl) {
          window.location.href = pay.payUrl
          return
        }
        showErrorToast(getVipOrderPaymentFailureMessage(order))
      } catch (e) {
        const msg = getApiErrorMessage(e, '充值下单失败')
        this.error = msg
        showErrorToast(msg)
      } finally {
        this.submitting = false
      }
    },
    startPayPoll() {
      this.stopPayPoll()
      if (!this.payVisible || this.payOrderId == null || this.payOrderId === '') return
      this.checkPayOnce()
      this.payPollTimer = window.setInterval(() => this.checkPayOnce(), PAY_POLL_MS)
    },
    stopPayPoll() {
      if (this.payPollTimer != null) {
        clearInterval(this.payPollTimer)
        this.payPollTimer = null
      }
      this.payPollInFlight = false
    },
    async checkPayOnce() {
      if (!this.payVisible || this.payOrderId == null || this.payOrderId === '') return
      if (this.payPollInFlight) return
      this.payPollInFlight = true
      try {
        const res = await scanAlipayPayment(this.payOrderId)
        if (isAlipayPaymentPaid(res)) {
          this.onPaid()
        }
      } catch (e) {
        /* 轮询失败忽略，继续下次 */
      } finally {
        this.payPollInFlight = false
      }
    },
    onPaid() {
      this.stopPayPoll()
      this.payVisible = false
      this.payOrderId = null
      showSuccessToast('支付成功，积分已到账')
      this.loadProfile()
      try {
        window.dispatchEvent(new CustomEvent('opengate-balance-refresh'))
      } catch (e) {
        /* ignore */
      }
    },
    closePay() {
      this.payVisible = false
      this.stopPayPoll()
      this.payOrderId = null
    },
    onPayExpired() {
      this.closePay()
      showErrorToast('支付已超时，请重新下单')
    }
  }
}
</script>

<style scoped>
.recharge__balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  margin-bottom: 28px;
}
.recharge__label {
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 6px;
}
.recharge__score {
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: var(--accent);
}
.recharge__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
@media (min-width: 720px) {
  .recharge__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.pack {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 18px 16px;
  border: 1px solid var(--line);
  background: var(--bg-panel);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.pack:hover {
  border-color: var(--line-strong);
}
.pack.is-selected {
  border-color: var(--accent);
  background: var(--accent-dim);
  box-shadow: 0 0 0 1px rgba(125, 255, 179, 0.25);
}
.pack.is-featured {
  grid-column: span 2;
}
.pack__badge {
  position: absolute;
  right: 10px;
  top: 10px;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 6px;
  background: #fbbf24;
  color: #1a1208;
}
.pack__points {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
}
.pack__old {
  margin-right: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--muted);
  text-decoration: line-through;
}
.pack__unit {
  font-size: 12px;
  color: var(--muted);
}
.pack__price {
  margin-top: 10px;
  align-self: flex-end;
  font-size: 15px;
  font-weight: 700;
}
.recharge__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}
.recharge__note {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.6;
}
.recharge__note a {
  color: var(--accent);
}
.flash {
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
}
.flash.is-err {
  background: rgba(255, 107, 107, 0.12);
  color: #ff8f8f;
}
.muted {
  color: var(--muted);
  font-size: 14px;
}
</style>
